import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI, expenseAPI, employeeAPI } from '../lib/api';
import { formatINR, formatINRShort, formatPercent, formatDate, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DatePicker from '../components/DatePicker';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Trash2,
  Loader2,
  PieChart,
  BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, LineChart, Line, LabelList } from 'recharts';

const EXPENSE_CATEGORIES = [
  'Rent',
  'Utilities',
  'Software',
  'Grocery',
  'Travel',
  'Equipment',
  'Marketing',
  'Insurance',
  'Legal',
  'Other'
];

// Expense categories arrive fragmented across sources (Zoho chart-of-accounts,
// Sleekbill, bank/cash) — e.g. "salaries", "Salaries and Employee Welfare",
// "Vendor Bill", "Vendors Payment". Fold them into a clean, consistent taxonomy
// so the chart is readable instead of 20+ overlapping slivers.
const normalizeExpenseCategory = (raw) => {
  const c = (raw || '').toLowerCase().trim();
  if (!c) return 'Other';
  if (c.includes('director') || c.includes('remunerat')) return 'Director Remuneration';
  if (c.includes('salar') || c.includes('employee welfare') || c.includes('wages')) return 'Salaries';
  if (c.includes('rent')) return 'Rent';
  if (c.includes('market') || c.includes('advertis')) return 'Sales & Marketing';
  if (c.includes('computer') || c.includes('laptop')) return 'Computers & Laptops';
  if (c.includes('it equip') || c.includes('it expense') || c === 'it') return 'IT';
  if (c.includes('office equip') || c.includes('furnitur') || c.includes('fixture')) return 'Office Equipment';
  if (c.includes('vendor') || c.includes('purchase')) return 'Vendor Payments';
  if (c.includes('electric') || c.includes('utilit') || c.includes('water') || c.includes('power')) return 'Utilities';
  if (c.includes('internet') || c.includes('connectiv') || c.includes('telephone')) return 'Internet & Telecom';
  if (c.includes('invest') || c.includes('mutual')) return 'Investments';
  if (c.includes('tax')) return 'Taxes & Duties';
  if (c.includes('bad debt')) return 'Bad Debt';
  if (c.includes('travel') || c.includes('fuel') || c.includes('petrol')) return 'Travel & Fuel';
  if (c.includes('subscription')) return 'Subscriptions';
  if (c.includes('insur')) return 'Insurance';
  if (c.includes('legal') || c.includes('professional') || c.includes('consult')) return 'Legal & Professional';
  if (c.includes('bank') || c.includes('charge') || c.includes('chrg')) return 'Bank Charges';
  if (c.includes('misc') || c.includes('no track')) return 'Miscellaneous';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

// Normalize → aggregate → sort → keep top N, lump the rest into "Other".
const consolidateExpenses = (rows, topN = 12) => {
  const map = {};
  (rows || []).forEach((r) => {
    const k = normalizeExpenseCategory(r.category);
    map[k] = (map[k] || 0) + (r.amount || 0);
  });
  const sorted = Object.entries(map)
    .map(([category, amount]) => ({ category, amount }))
    .filter((r) => r.amount > 0)
    .sort((a, b) => b.amount - a.amount);
  if (sorted.length <= topN) return sorted;
  const top = sorted.slice(0, topN);
  const other = sorted.slice(topN).reduce((s, r) => s + r.amount, 0);
  if (other > 0) top.push({ category: 'Other', amount: other });
  return top;
};

const PAYMENT_METHODS = ['Bank', 'Credit Card', 'Cash', 'UPI'];

export default function Financials() {
  const { can } = useAuth();
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [expenseForm, setExpenseForm] = useState({
    category: '',
    vendor: '',
    description: '',
    amount: '',
    payment_date: '',
    due_date: '',
    payment_method: 'Bank',
    is_recurring: false,
    frequency: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, revenueRes, expenseRes, expensesListRes, trendsRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRevenueBreakdown(),
        dashboardAPI.getExpenseBreakdown(),
        expenseAPI.getAll(),
        dashboardAPI.getMonthlyTrends(),
      ]);

      setStats(statsRes.data);
      setRevenueData(revenueRes.data);
      setExpenseData(expenseRes.data);
      setExpenses(expensesListRes.data);
      setMonthlyTrends(trendsRes.data);
    } catch (error) {
      toast.error(apiError(error, 'Failed to fetch financial data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setExpenseForm({
      category: '',
      vendor: '',
      description: '',
      amount: '',
      payment_date: '',
      due_date: '',
      payment_method: 'Bank',
      is_recurring: false,
      frequency: ''
    });
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseForm.category) return toast.error('Pick a category');
    setSaving(true);

    try {
      const data = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount)
      };
      await expenseAPI.create(data);
      toast.success('Expense added successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to add expense'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await expenseAPI.delete(id);
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to delete expense'));
    }
  };

  const pieColors = ['#000000', '#333333', '#555555', '#777777', '#999999', '#bbbbbb'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);
  const fixedExpenses = expenses.filter(e => e.is_recurring).reduce((sum, e) => sum + e.amount, 0);
  const variableExpenses = totalExpenses - fixedExpenses;

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Financials</h1>
          <p className="text-gray-500 mt-1">Track revenue, expenses, and profitability</p>
        </div>
        {can('write') && (
        <div className="flex items-center gap-3">
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-expense-btn" className="bg-black hover:bg-black/90">
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Add New Expense</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleExpenseSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={expenseForm.category} 
                    onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}
                  >
                    <SelectTrigger data-testid="expense-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor">Vendor *</Label>
                  <Input
                    id="vendor"
                    data-testid="expense-vendor-input"
                    value={expenseForm.vendor}
                    onChange={(e) => setExpenseForm({ ...expenseForm, vendor: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  data-testid="expense-desc-input"
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount (₹) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    data-testid="expense-amount-input"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="paymentDate">Payment Date *</Label>
                  <DatePicker
                    id="paymentDate"
                    data-testid="expense-date-input"
                    value={expenseForm.payment_date}
                    onChange={(v) => setExpenseForm({ ...expenseForm, payment_date: v })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="paymentMethod">Payment Method</Label>
                  <Select 
                    value={expenseForm.payment_method} 
                    onValueChange={(v) => setExpenseForm({ ...expenseForm, payment_method: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method} value={method}>{method}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={expenseForm.is_recurring}
                    onChange={(e) => setExpenseForm({ ...expenseForm, is_recurring: e.target.checked })}
                  />
                  <span className="text-sm">Recurring expense</span>
                </label>
                {expenseForm.is_recurring && (
                  <Select 
                    value={expenseForm.frequency} 
                    onValueChange={(v) => setExpenseForm({ ...expenseForm, frequency: v })}
                  >
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Quarterly">Quarterly</SelectItem>
                      <SelectItem value="Yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="bg-black hover:bg-black/90">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Expense'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
        )}
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide">Total Revenue</p>
                  <p className="text-2xl font-bold font-mono mt-2">{formatINR(stats.total_revenue)}</p>
                </div>
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide">Total Expenses</p>
                  <p className="text-2xl font-bold font-mono mt-2">{formatINR(stats.total_expenses)}</p>
                </div>
                <div className="p-2 bg-gray-100 rounded-lg">
                  <TrendingDown className="w-5 h-5 text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide">Net Profit</p>
                  <p className={`text-2xl font-bold font-mono mt-2 ${stats.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatINR(stats.net_profit)}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${stats.net_profit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <DollarSign className={`w-5 h-5 ${stats.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide">Net Margin</p>
                  <p className={`text-2xl font-bold font-mono mt-2 ${
                    stats.margin_percentage >= 30 ? 'text-green-700' : 
                    stats.margin_percentage >= 20 ? 'text-orange-600' : 'text-red-700'
                  }`}>
                    {formatPercent(stats.margin_percentage)}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${
                  stats.margin_percentage >= 30 ? 'bg-green-100' : 
                  stats.margin_percentage >= 20 ? 'bg-orange-100' : 'bg-red-100'
                }`}>
                  <BarChart3 className={`w-5 h-5 ${
                    stats.margin_percentage >= 30 ? 'text-green-700' : 
                    stats.margin_percentage >= 20 ? 'text-orange-600' : 'text-red-700'
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="expenses-tab">Expenses</TabsTrigger>
          <TabsTrigger value="breakdown" data-testid="breakdown-tab">Breakdown</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Trends */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Revenue Trends</CardTitle>
              </CardHeader>
              <CardContent>
                {monthlyTrends.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyTrends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={formatINRShort} />
                      <Tooltip 
                        formatter={(value) => formatINR(value)}
                        contentStyle={{ backgroundColor: '#000', border: 'none', borderRadius: '8px', color: '#fff' }}
                      />
                      <Line type="monotone" dataKey="revenue" stroke="#000000" strokeWidth={2} dot={{ fill: '#000' }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-400">
                    No trend data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expense by Category */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Expenses by Category</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const rows = consolidateExpenses(expenseData?.by_category);
                  const total = rows.reduce((s, r) => s + r.amount, 0) || 1;
                  const data = rows.map((r) => ({
                    ...r,
                    name: r.category.length > 22 ? r.category.slice(0, 22) + '…' : r.category,
                    pct: Math.round((r.amount / total) * 100),
                  }));
                  return data.length > 0 ? (
                    <ResponsiveContainer width="100%" height={Math.max(300, data.length * 32)}>
                      <BarChart data={data} layout="vertical" margin={{ left: 10, right: 56, top: 4, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" horizontal={false} />
                        <XAxis type="number" tickFormatter={formatINRShort} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: '#444' }} interval={0} />
                        <Tooltip
                          formatter={(value) => formatINR(value)}
                          labelFormatter={(_, p) => p?.[0]?.payload?.category || ''}
                          contentStyle={{ backgroundColor: '#000', border: 'none', borderRadius: '8px', color: '#fff' }}
                        />
                        <Bar dataKey="amount" fill="#000000" radius={[0, 4, 4, 0]} maxBarSize={22}>
                          <LabelList dataKey="pct" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 11, fill: '#666' }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-gray-400">
                      No expense data yet
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {/* Expense Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
            <Card>
              <CardContent className="p-6">
                <p className="text-sm text-gray-500">Total Office Expenses</p>
                <p className="text-2xl font-bold font-mono mt-1">{formatINR(totalExpenses)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <p className="text-sm text-gray-500">Fixed Costs (Recurring)</p>
                <p className="text-2xl font-bold font-mono mt-1">{formatINR(fixedExpenses)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <p className="text-sm text-gray-500">Salaries</p>
                <p className="text-2xl font-bold font-mono mt-1">{formatINR(stats?.total_salaries || 0)}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="mt-6">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th className="table-header text-left">Category</th>
                      <th className="table-header text-left">Vendor</th>
                      <th className="table-header text-left">Description</th>
                      <th className="table-header text-right">Amount</th>
                      <th className="table-header text-left">Payment Date</th>
                      <th className="table-header text-center">Type</th>
                      <th className="table-header text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.length > 0 ? (
                      expenses.map((expense) => (
                        <tr key={expense.id} className="table-row" data-testid={`expense-row-${expense.id}`}>
                          <td className="table-cell">
                            <span className="badge badge-gray">{expense.category}</span>
                          </td>
                          <td className="table-cell font-medium">{expense.vendor}</td>
                          <td className="table-cell text-gray-600">{expense.description || '-'}</td>
                          <td className="table-cell text-right font-mono">{formatINR(expense.amount)}</td>
                          {/* Recurring bills store a day like "10th", which formatDate turns into "Invalid Date". */}
                          <td className="table-cell text-gray-600">{/^\d{4}-\d{2}-\d{2}/.test(expense.payment_date || '') ? formatDate(expense.payment_date) : (expense.payment_date || '-')}</td>
                          <td className="table-cell text-center">
                            {expense.is_recurring ? (
                              <span className="badge badge-success">{expense.frequency}</span>
                            ) : (
                              <span className="badge badge-gray">One-time</span>
                            )}
                          </td>
                          <td className="table-cell text-center">
                            {can('delete') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteExpense(expense.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-12 text-gray-400">
                          No expenses recorded yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Breakdown Tab */}
        <TabsContent value="breakdown" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue by Client */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Revenue by Client</CardTitle>
              </CardHeader>
              <CardContent>
                {revenueData?.by_client?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={revenueData.by_client.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis type="number" tickFormatter={formatINRShort} />
                      <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                      <Tooltip 
                        formatter={(value) => formatINR(value)}
                        contentStyle={{ backgroundColor: '#000', border: 'none', borderRadius: '8px', color: '#fff' }}
                      />
                      <Bar dataKey="revenue" fill="#000000" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-400">
                    No revenue data yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Revenue by Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Pipeline by Status</CardTitle>
              </CardHeader>
              <CardContent>
                {revenueData?.by_status?.length > 0 ? (
                  <div className="space-y-4">
                    {revenueData.by_status.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium capitalize">{item.status}</p>
                          <p className="text-sm text-gray-500">{item.count} proposals</p>
                        </div>
                        <p className="font-mono font-bold">{formatINR(item.value)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-400">
                    No status data yet
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
